import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Grid,
  Paper,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Skeleton,
  IconButton,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import {
  NotificationsActive,
  AssignmentTurnedIn,
  LocalShipping,
  Timer,
  CheckCircle,
  History,
  Storefront,
  Person,
  Search,
  Visibility,
  MonetizationOn,
  Cancel,
  ArrowBack,
  ArrowForward,
  AccessTime,
  LocationOn,
  Payment,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import api from "../../api/axios";
import socket from "../../socket";
import { useSnackbar } from "../../utils/SnackbarProvider";
import { requestNotificationPermission, showBrowserNotification } from "../../utils/pushNotification";
import { useNotificationTabCoordinator } from "../../utils/notificationTabCoordinator";
import LoadingScreen from "../../components/LoadingScreen";
import { useNotificationSocketHandler } from '../../utils/useNotificationSocketHandler';

/* ── sessionStorage keys ── */
const CACHE_KEY_ORDERS = "agentOrders";
const CACHE_KEY_NOTIFS = "agentNotifs";

/* ── Helpers ── */
function formatCurrency(value) {
  if (typeof value === "number") return `₹${value.toLocaleString("en-IN")}`;
  if (value === 0) return "₹0";
  return value || "—";
}

function getTimeRemaining(expiresAt, now = Date.now()) {
  if (!expiresAt) return "N/A";
  const expires = new Date(expiresAt);
  if (Number.isNaN(expires.getTime())) return "N/A";
  const diff = expires.getTime() - now;
  if (diff <= 0) return "Expired";

  const totalSeconds = Math.max(0, Math.floor(diff / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
}

function getNotificationDuration(notification) {
  const expiresAt = notification.expiresAt ? new Date(notification.expiresAt) : null;
  if (!expiresAt || Number.isNaN(expiresAt.getTime())) return null;

  const startAt = notification.sentAt
    ? new Date(notification.sentAt)
    : notification.createdAt
    ? new Date(notification.createdAt)
    : new Date();

  if (Number.isNaN(startAt.getTime())) {
    return {
      remainingMs: Math.max(0, expiresAt.getTime() - Date.now()),
      totalMs: Math.max(0, expiresAt.getTime() - Date.now()),
    };
  }

  const remainingMs = Math.max(0, expiresAt.getTime() - Date.now());
  const totalMs = Math.max(1, expiresAt.getTime() - startAt.getTime());

  return { remainingMs, totalMs };
}

function getTimeRemainingPercent(notification, now = Date.now()) {
  const duration = getNotificationDuration(notification);
  if (!duration) return 100;
  if (duration.totalMs <= 0) return 0;
  return Math.max(0, Math.min(100, (duration.remainingMs / duration.totalMs) * 100));
}

const AgentOrders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const agent = JSON.parse(localStorage.getItem("user"));
  const { showSnackbar } = useSnackbar();

  const [tabValue, setTabValue] = useState(() => {
    // Check query param first (used by Service Worker notification clicks)
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'notifications') return 0;
    return location.state?.activeTab || 0;
  });
  const [notifications, setNotifications] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(CACHE_KEY_NOTIFS) || "[]");
    } catch {
      return [];
    }
  });
  const [acceptedOrders, setAcceptedOrders] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(CACHE_KEY_ORDERS) || "[]");
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [clockTick, setClockTick] = useState(Date.now());
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAvailable, setIsAvailable] = useState(true);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [agentName, setAgentName] = useState(() => {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return u?.name || u?.firstName || u?.username || "";
  });
  const { isNotificationOwner } = useNotificationTabCoordinator();

  // ⛔ Protect agent route
  useEffect(() => {
    if (!agent?._id) {
      navigate("/agent/login", { replace: true });
      return;
    }
    socket.emit("agent_join", agent._id);
    return () => {
      socket.emit("agent_leave", agent._id);
    };
  }, [agent, navigate]);

  // 🔔 Fetch pending notifications
  const fetchNotifications = useCallback(async () => {
    if (!agent?._id) return;
    try {
      setLoading(true);
      const { data } = await api.get("/agent/notifications/pending");
      const notifs = data.notifications || [];
      setNotifications(notifs);
      sessionStorage.setItem(CACHE_KEY_NOTIFS, JSON.stringify(notifs));
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [agent?._id]);

  // 📦 Fetch all orders (active + delivered)
  const fetchAcceptedOrders = useCallback(async () => {
    if (!agent?._id) return;
    try {
      const { data } = await api.get("/agent/orders/accepted");
      const orders = data.orders || [];
      setAcceptedOrders(orders);
      sessionStorage.setItem(CACHE_KEY_ORDERS, JSON.stringify(orders));
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    }
  }, [agent?._id]);

  // Fetch agent profile for availability
  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/agent/profile");
      if (data?.profile) {
        setIsAvailable(data.profile.isAvailable ?? true);
        if (data.profile.name) setAgentName(data.profile.name);
      }
    } catch (err) {
      console.warn("Failed to fetch profile:", err);
    }
  }, []);

  // Split orders into active (not delivered) and delivered
  const activeOrders = acceptedOrders.filter(
    (o) => o.deliveryPhase?.status !== "DELIVERED"
  );
  const deliveredOrders = acceptedOrders.filter(
    (o) => o.deliveryPhase?.status === "DELIVERED"
  );

  // Filter by search
  const filterBySearch = (orders) => {
    if (!searchQuery) return orders;
    const q = searchQuery.toLowerCase();
    return orders.filter((o) => {
      const id = o._id?.toLowerCase() || "";
      const items = (o.items || []).map((i) => (i.title || "").toLowerCase()).join(" ");
      const vendor = o.vendorName?.toLowerCase() || "";
      const address = o.address?.addressLine1?.toLowerCase() || "";
      return id.includes(q) || items.includes(q) || vendor.includes(q) || address.includes(q);
    });
  };

  const filteredNotifications = filterBySearch(notifications);
  const filteredActive = filterBySearch(activeOrders);
  const filteredDelivered = filterBySearch(deliveredOrders);

  // Initial fetch — skip loading screen if cached data exists (e.g. navigating back from order details)
  useEffect(() => {
    if (agent?._id) {
      const hasCachedOrders = sessionStorage.getItem(CACHE_KEY_ORDERS);
      const hasCachedNotifs = sessionStorage.getItem(CACHE_KEY_NOTIFS);
      if (hasCachedOrders && hasCachedNotifs) {
        setInitialLoading(false); // Show cached data immediately, refresh in background
      }
      Promise.all([
        fetchNotifications(),
        fetchAcceptedOrders(),
        fetchProfile(),
      ]).finally(() => setInitialLoading(false));
    }
  }, [agent?._id, fetchNotifications, fetchAcceptedOrders, fetchProfile]);

  const audioRef = useRef(null);

  useEffect(() => {
    const interval = window.setInterval(() => setClockTick(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    audioRef.current = new Audio("/bell.mp3");
    audioRef.current.volume = 0.5;
    void requestNotificationPermission();
  }, []);

  const playNotificationSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((err) => {
        console.warn("Unable to play notification sound:", err);
      });
    }
  }, []);

  useNotificationSocketHandler({
    role: 'agent',
    isNotificationOwner,
    onNewOrder: () => {
      playNotificationSound();
    },
    onOrderTaken: (data) => {
      setNotifications((prev) => prev.filter((n) => n.orderId !== data.orderId));
    },
    updatePendingOrders: fetchNotifications,
  });

  // ✅ Accept order
  const handleAcceptOrder = async () => {
    if (!selectedNotification) return;
    try {
      setLoading(true);
      await api.post(
        `/agent/notifications/${selectedNotification.orderId}/accept`
      );
      setNotifications((prev) =>
        prev.filter((n) => n.orderId !== selectedNotification.orderId)
      );
      await fetchAcceptedOrders();
      setConfirmDialog(false);
      setSelectedNotification(null);
      setDialogAction(null);
      showSnackbar("Order accepted successfully!", "success");
    } catch (error) {
      console.error("Failed to accept order:", error);
      showSnackbar(
        error.response?.data?.message || "Failed to accept order",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // ❌ Decline order
  const handleDeclineOrder = async () => {
    if (!selectedNotification) return;
    try {
      setLoading(true);
      await api.post(
        `/agent/notifications/${selectedNotification.orderId}/decline`
      );
      setNotifications((prev) =>
        prev.filter((n) => n.orderId !== selectedNotification.orderId)
      );
      setConfirmDialog(false);
      setSelectedNotification(null);
      setDialogAction(null);
    } catch (error) {
      console.error("Failed to decline order:", error);
      showSnackbar(
        error.response?.data?.message || "Failed to decline order",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // 🔄 Navigate to order details
  const goToOrderDetails = (orderId) => {
    navigate(`/agent/orders/${orderId}`, { state: { fromTab: tabValue } });
  };

  // 🔄 Toggle availability
  const handleToggleAvailability = useCallback(async () => {
    try {
      setAvailabilityLoading(true);
      const newStatus = !isAvailable;
      const { data } = await api.put("/agent/availability", {
        isAvailable: newStatus,
      });
      if (data.success) {
        setIsAvailable(data.isAvailable);
        showSnackbar(data.message, "success");
      }
    } catch (err) {
      console.error("Toggle availability error:", err);
      showSnackbar("Failed to update availability", "error");
    } finally {
      setAvailabilityLoading(false);
    }
  }, [isAvailable, showSnackbar]);

  // ── Helpers ──
  const getNotificationVendorName = (notification) => {
    return (
      notification.vendorName ||
      notification.vendor?.name ||
      notification.vendor?.storeName ||
      notification.vendorCity ||
      "New Order"
    );
  };

  const getNotificationAmount = (notification) => {
    return notification.totalAmount ?? notification.amount ?? 0;
  };

  const getNotificationItemCount = (notification) => {
    return notification.itemCount ?? notification.items?.length ?? 0;
  };

  const getNotificationItems = (notification) => {
    return notification.items ?? [];
  };

  const normalizeStatusValue = (value = "") => {
    return String(value || "")
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  const getCurrentOrderStatus = (order) => {
    const status = order.deliveryPhase?.status || order.status;
    let normalizedStatus =
      !status || status === "NONE" || status === "UNKNOWN"
        ? "PENDING"
        : normalizeStatusValue(status);

    const tracking = Array.isArray(order.tracking) ? order.tracking : [];
    if (tracking.length > 0) {
      const lastTracking = tracking[tracking.length - 1];
      const candidate =
        lastTracking?.status ||
        lastTracking?.label ||
        lastTracking?.key ||
        lastTracking?.code;
      const normalizedTracking = normalizeStatusValue(candidate);
      if (
        [
          "VENDOR_READY",
          "READY_FOR_PICKUP",
          "ITEMS_READY_FOR_PICKUP",
        ].includes(normalizedTracking)
      ) {
        normalizedStatus = "ASSIGNED";
      }
    }

    if (
      ["VENDOR_READY", "READY_FOR_PICKUP", "ITEMS_READY_FOR_PICKUP"].includes(
        normalizedStatus
      )
    ) {
      normalizedStatus = "ASSIGNED";
    }

    return normalizedStatus;
  };

  const getOrderStatusChip = (order) => {
    const normalizedStatus = getCurrentOrderStatus(order);
    const label = normalizedStatus.replace(/_/g, " ");
    let color = "default";
    if (normalizedStatus === "DELIVERED") color = "success";
    else if (
      normalizedStatus === "IN_TRANSIT" ||
      normalizedStatus === "PICKED_UP"
    )
      color = "info";
    else if (normalizedStatus === "PENDING" || normalizedStatus === "ASSIGNED")
      color = "warning";
    return (
      <Chip
        icon={<CheckCircle />}
        label={label}
        color={color}
        variant="outlined"
        size="small"
        sx={{ fontWeight: 600, fontSize: "0.7rem" }}
      />
    );
  };

  const getVendorDetails = (order) => {
    if (!order) return { name: "—", address: null, addressData: null };

    const orderVendor = order.vendorId || order.vendor;
    const itemVendor = (order.items || [])
      .map((item) => item.vendor || item.vendorId)
      .find(Boolean);
    const vendor = orderVendor || itemVendor;

    const store = vendor?.store || vendor?.vendor?.store || null;
    const name =
      store?.businessName ||
      vendor?.store?.businessName ||
      vendor?.name ||
      vendor?.storeName ||
      order?.vendorName ||
      "—";

    const addressData = store?.address || vendor?.address || null;
    const formattedAddress = addressData
      ? [addressData.addressLine1, addressData.addressLine2, addressData.city, addressData.state, addressData.pincode, addressData.country]
          .filter(Boolean)
          .join(", ") || null
      : null;

    return { name, address: formattedAddress, addressData };
  };

  const getVendorName = (order) => {
    return getVendorDetails(order).name;
  };

  const getVendorAddress = (order) => {
    return getVendorDetails(order).address;
  };

  const getCustomerName = (order) => {
    const user = order.userId || order.customer;
    if (!user || typeof user === "string") return "—";
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
    return name || user.name || user.username || "—";
  };

  const getDeliveryAddress = (order) => {
    const addr = order.address || order.shippingAddress || order.deliveryAddress;
    if (!addr) return "—";
    return [addr.addressLine1, addr.city].filter(Boolean).join(", ") || "—";
  };

  const formatAddressForMaps = (address) => {
    if (!address) return null;
    return [
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.state,
      address.pincode,
      address.country,
    ].filter(Boolean).join(", ");
  };

  const openGoogleMapsDirections = useCallback((address) => {
    const destination = formatAddressForMaps(address);
    if (!destination) return;

    const buildMapsUrl = (origin) => {
      const encodedDestination = encodeURIComponent(destination);
      const encodedOrigin = encodeURIComponent(origin);
      return `https://www.google.com/maps/dir/?api=1&origin=${encodedOrigin}&destination=${encodedDestination}&travelmode=driving`;
    };

    const fallbackSearch = () => {
      const searchUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(destination)}`;
      window.open(searchUrl, "_blank", "noopener,noreferrer");
    };

    if (!navigator.geolocation) {
      fallbackSearch();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const origin = `${position.coords.latitude},${position.coords.longitude}`;
        window.open(buildMapsUrl(origin), "_blank", "noopener,noreferrer");
      },
      () => fallbackSearch(),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      {/* ═══════════════ GRADIENT HEADER ═══════════════ */}
      <Box sx={{ background: "linear-gradient(135deg, #0f172a 0%, #0062ff 100%)", px: { xs: 2, sm: 4 }, py: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 0.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Button onClick={() => navigate("/agent/dashboard")} sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none", fontSize: "0.82rem", fontWeight: 500, mb: 0.5, p: 0, "&:hover": { color: "#fff", bgcolor: "transparent" }, display: "flex", alignItems: "center", gap: 0.5 }}>
              <ArrowBack sx={{ fontSize: 16 }} />
              Back to Dashboard
            </Button>
            <Typography variant="h5" fontWeight={700} sx={{ color: "#fff" }}>📋 Orders</Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", mt: 0.25 }}>View notifications and manage your deliveries</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="caption" sx={{ color: isAvailable ? "#4ade80" : "rgba(255,255,255,0.5)", fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>{isAvailable ? "Active" : "Inactive"}</Typography>
            <Switch checked={isAvailable} onChange={handleToggleAvailability} disabled={availabilityLoading} size="small" sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#4ade80" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#4ade80" }, "& .MuiSwitch-track": { backgroundColor: "rgba(255,255,255,0.3)" }, "& .MuiSwitch-thumb": { backgroundColor: "#fff" } }} />
          </Box>
        </Box>
      </Box>

      {initialLoading ? (
        <Box sx={{ px: { xs: 2, sm: 4 }, pb: 4, mt: 2 }}>
          <LoadingScreen message="Loading your orders..." />
        </Box>
      ) : (
      <Box sx={{ px: { xs: 2, sm: 4 }, pb: 4 }}>
        {/* ═══════════════ SEARCH BAR ═══════════════ */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            mt: 2,
            p: 1.5,
            bgcolor: "#fff",
            borderRadius: 2,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          }}
        >
          <Search sx={{ fontSize: 20, color: "#94a3b8" }} />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by order ID, items, vendor, or address..."
            style={{
              background: "transparent",
              border: "none",
              outline: "none",
              color: "#1e293b",
              fontSize: "0.85rem",
              width: "100%",
              caretColor: "#1976d2",
            }}
          />
          {searchQuery && (
            <IconButton
              size="small"
              onClick={() => setSearchQuery("")}
              sx={{ color: "#94a3b8" }}
            >
              <Cancel sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>

        {/* ═══════════════ TABS ═══════════════ */}
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: "12px 12px 0 0",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
              px: 2,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={(e, val) => setTabValue(val)}
              sx={{
                minHeight: 48,
                "& .MuiTabs-indicator": {
                  backgroundColor: "#4ade80",
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                },
                "& .MuiTab-root": {
                  color: "rgba(255,255,255,0.5)",
                  minHeight: 48,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  "&.Mui-selected": {
                    color: "#fff",
                  },
                },
              }}
            >
              <Tab
                icon={
                  <Badge
                    badgeContent={notifications.length}
                    color="error"
                    sx={{
                      "& .MuiBadge-badge": {
                        animation:
                          notifications.length > 0
                            ? "pulse 2s infinite"
                            : "none",
                        "@keyframes pulse": {
                          "0%": { transform: "scale(1)" },
                          "50%": { transform: "scale(1.1)" },
                          "100%": { transform: "scale(1)" },
                        },
                      },
                    }}
                  >
                    <NotificationsActive sx={{ fontSize: 18 }} />
                  </Badge>
                }
                label="Notifications"
                iconPosition="start"
              />
              <Tab
                icon={
                  <Badge
                    badgeContent={activeOrders.length}
                    color="primary"
                    sx={{
                      "& .MuiBadge-badge": {
                        bgcolor: "#3b82f6",
                      },
                    }}
                  >
                    <AssignmentTurnedIn sx={{ fontSize: 18 }} />
                  </Badge>
                }
                label="Active Orders"
                iconPosition="start"
              />
              <Tab
                icon={
                  <Badge
                    badgeContent={deliveredOrders.length}
                    color="success"
                    sx={{
                      "& .MuiBadge-badge": {
                        bgcolor: "#22c55e",
                      },
                    }}
                  >
                    <History sx={{ fontSize: 18 }} />
                  </Badge>
                }
                label="Delivered"
                iconPosition="start"
              />
            </Tabs>
          </Box>
        </Box>

        {/* ═══════════════ NOTIFICATIONS TAB ═══════════════ */}
        {tabValue === 0 && (
          <Box sx={{ mt: 2 }}>
            {filteredNotifications.length === 0 ? (
              <Paper
                sx={{
                  p: 6,
                  textAlign: "center",
                  borderRadius: 3,
                  bgcolor: "#fff",
                  boxShadow: "0 2px 12px rgba(15,23,42,0.07)",
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    bgcolor: "rgba(25,118,210,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <NotificationsActive
                    sx={{ fontSize: 36, color: "#1976d2" }}
                  />
                </Box>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ color: "#1e293b", mb: 0.5 }}
                >
                  {searchQuery
                    ? "No matching notifications"
                    : "No new order notifications"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                  {searchQuery
                    ? "Try a different search term"
                    : "New orders will appear here when available"}
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={2.5}>
                {filteredNotifications.map((notification) => {
                  const timeLeft = getTimeRemaining(notification.expiresAt, clockTick);
                  const timePercent = getTimeRemainingPercent(notification, clockTick);
                  const isExpired = notification.isExpired || timeLeft === "Expired";
                  const isUrgent = !isExpired && timePercent < 30;

                  return (
                    <Card
                      key={notification.orderId}
                      sx={{
                        borderRadius: 3.2,
                        boxShadow: "0 14px 40px rgba(15,23,42,0.06)",
                        border: "1px solid #eceff4",
                        position: "relative",
                        overflow: "visible",
                        transition: "transform 0.18s ease, box-shadow 0.18s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 18px 36px rgba(15,23,42,0.09)",
                        },
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 4,
                          borderRadius: "12px 0 0 12px",
                          background: isExpired
                            ? "linear-gradient(180deg, #94a3b8, #cbd5e1)"
                            : isUrgent
                            ? "linear-gradient(180deg, #ef4444, #f97316)"
                            : "linear-gradient(180deg, #3b82f6, #8b5cf6)",
                        },
                        opacity: isExpired ? 0.7 : 1,
                      }}
                    >
                      <CardContent sx={{ p: { xs: 2, sm: 2.1 } }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#111827", fontSize: { xs: "0.95rem", sm: "1rem" }, letterSpacing: "-0.01em" }}>
                            New delivery request
                          </Typography>
                          <Chip
                            icon={<Timer />}
                            label={timeLeft}
                            size="small"
                            color={isExpired ? "default" : isUrgent ? "error" : "warning"}
                            variant={isExpired ? "outlined" : "filled"}
                            sx={{ height: 24, fontSize: "0.68rem", fontWeight: 700, bgcolor: isExpired ? "#f5f7fa" : isUrgent ? "#fef2f2" : "#fef3c7", color: isExpired ? "#64748b" : isUrgent ? "#b91c1c" : "#92400e", border: "1px solid rgba(15,23,42,0.06)", "& .MuiChip-icon": { color: "inherit" } }}
                          />
                        </Box>

                        {!isExpired ? (
                          <Box sx={{ mb: 1.1 }}>
                            <Box
                              sx={{
                                height: 4,
                                borderRadius: 999,
                                bgcolor: "#eef2f7",
                                overflow: "hidden",
                                position: "relative",
                                boxShadow: "inset 0 1px 2px rgba(15,23,42,0.06)",
                              }}
                            >
                              <Box
                                sx={{
                                  height: "100%",
                                  width: `${Math.max(0, Math.min(100, timePercent))}%`,
                                  borderRadius: 999,
                                  background: isUrgent
                                    ? "linear-gradient(90deg, #ef4444 0%, #fb923c 100%)"
                                    : "linear-gradient(90deg, #2563eb 0%, #60a5fa 100%)",
                                  transition: "width 1s linear",
                                  boxShadow: "0 0 10px rgba(37, 99, 235, 0.16)",
                                }}
                              />
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ mb: 1.1, height: 3.2, borderRadius: 999, bgcolor: "#f1f5f9" }} />
                        )}

                        <Grid container spacing={1.2} alignItems="stretch" sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}>
                          <Grid item xs={12} md={3} sx={{ minWidth: 0, flexBasis: { md: "25%" }, maxWidth: { md: "25%" } }}>
                            <Box
                              sx={{
                                height: "100%",
                                minHeight: 84,
                                borderRadius: 1.75,
                                overflow: "hidden",
                                bgcolor: "#f8fafc",
                                border: "1px solid #eceff4",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {notification.items?.[0]?.imageUrl ? (
                                <Box
                                  component="img"
                                  src={notification.items[0].imageUrl}
                                  alt={notification.items?.[0]?.title || "Product"}
                                  sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", maxWidth: 88, mx: "auto" }}
                                />
                              ) : (
                                <Box sx={{ fontSize: "1.35rem", color: "#94a3b8" }}>📦</Box>
                              )}
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={2} sx={{ minWidth: 0, flexBasis: { md: "16.66%" }, maxWidth: { md: "16.66%" } }}>
                            <Box sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", gap: 0.5 }}>
                              <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", fontSize: "0.66rem" }}>
                                Order count
                              </Typography>
                              <Typography variant="h6" fontWeight={700} sx={{ color: "#0f172a", lineHeight: 1.1, fontSize: "1rem" }}>
                                {getNotificationItemCount(notification)}
                              </Typography>
                              <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                                item{getNotificationItemCount(notification) !== 1 ? "s" : ""}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={4} sx={{ minWidth: 0, flexBasis: { md: "33.33%" }, maxWidth: { md: "33.33%" } }}>
                            <Box sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", gap: 1 }}>
                              <Box
                                sx={{ display: "flex", gap: 0.8, alignItems: "flex-start", minWidth: 0, cursor: "pointer" }}
                                onClick={() => openGoogleMapsDirections(notification.vendorAddress)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    openGoogleMapsDirections(notification.vendorAddress);
                                  }
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    bgcolor: "rgba(124,58,237,0.1)",
                                    flexShrink: 0,
                                    mt: 0.15,
                                  }}
                                >
                                  <Storefront sx={{ fontSize: 16, color: "#7c3aed" }} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", display: "block", mb: 0.08, fontSize: "0.66rem" }}>
                                    Pickup
                                  </Typography>
                                  <Typography variant="body2" fontWeight={600} sx={{ color: "#334155", lineHeight: 1.25, fontSize: "0.84rem" }}>
                                    {getNotificationVendorName(notification)}
                                  </Typography>
                                  {notification.vendorAddress && (
                                    <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", lineHeight: 1.3, wordBreak: "break-word", overflowWrap: "break-word" }}>
                                      {[notification.vendorAddress.addressLine1, notification.vendorAddress.city].filter(Boolean).join(", ")}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>

                              <Box
                                sx={{ display: "flex", gap: 0.8, alignItems: "flex-start", minWidth: 0, cursor: "pointer" }}
                                onClick={() => openGoogleMapsDirections(notification.customerAddress)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    openGoogleMapsDirections(notification.customerAddress);
                                  }
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    bgcolor: "rgba(25,118,210,0.1)",
                                    flexShrink: 0,
                                    mt: 0.15,
                                  }}
                                >
                                  <Person sx={{ fontSize: 16, color: "#1976d2" }} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", display: "block", mb: 0.08, fontSize: "0.66rem" }}>
                                    Deliver to
                                  </Typography>
                                  <Typography variant="body2" fontWeight={600} sx={{ color: "#334155", lineHeight: 1.25, fontSize: "0.84rem" }}>
                                    {notification.customerName || "Customer"}
                                  </Typography>
                                  {notification.customerAddress && (
                                    <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", lineHeight: 1.3, wordBreak: "break-word", overflowWrap: "break-word" }}>
                                      {[notification.customerAddress.addressLine1, notification.customerAddress.city].filter(Boolean).join(", ")}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={3} sx={{ minWidth: 0, flexBasis: { md: "25%" }, maxWidth: { md: "25%" } }}>
                            <Box sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: { xs: "flex-start", md: "flex-end" }, gap: 0.8 }}>
                              <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", fontSize: "0.66rem" }}>
                                Total value
                              </Typography>
                              <Typography variant="h6" fontWeight={700} sx={{ color: "#2563eb", lineHeight: 1.2, fontSize: "1rem" }}>
                                {formatCurrency(getNotificationAmount(notification))}
                              </Typography>
                              {notification.commission ? (
                                <Typography variant="caption" fontWeight={600} sx={{ color: "#16a34a" }}>
                                  Comm: {formatCurrency(notification.commission)}
                                </Typography>
                              ) : null}
                            </Box>
                          </Grid>
                        </Grid>

                        <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1.1, mt: 0.9, borderTop: "1px solid #eef2f7" }}>
                          {!isExpired ? (
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
                              <Button
                                variant="contained"
                                onClick={() => { setSelectedNotification(notification); setDialogAction("accept"); setConfirmDialog(true); }}
                                disabled={loading}
                                sx={{ bgcolor: "#16a34a", borderRadius: "999px", textTransform: "none", fontWeight: 700, fontSize: "0.8rem", px: 1.6, py: 0.7, minWidth: { xs: "100%", sm: 120 }, boxShadow: "0 8px 18px rgba(22,163,74,0.16)", "&:hover": { bgcolor: "#15803d", boxShadow: "0 10px 20px rgba(22,163,74,0.2)" } }}
                              >
                                Accept
                              </Button>
                              <Button
                                variant="outlined"
                                onClick={() => { setSelectedNotification(notification); setDialogAction("decline"); setConfirmDialog(true); }}
                                disabled={loading}
                                sx={{ borderColor: "#e2e8f0", color: "#b91c1c", borderRadius: "999px", textTransform: "none", fontWeight: 700, fontSize: "0.8rem", px: 1.6, py: 0.7, minWidth: { xs: "100%", sm: 100 }, bgcolor: "#fff7f7", "&:hover": { borderColor: "#dc2626", bgcolor: "#fef2f2" } }}
                              >
                                Decline
                              </Button>
                            </Stack>
                          ) : (
                            <Chip label="Expired" color="error" variant="outlined" sx={{ fontWeight: 600, fontSize: "0.75rem" }} />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}

        {/* ═══════════════ ACTIVE ORDERS TAB ═══════════════ */}
        {tabValue === 1 && (
          <Box sx={{ mt: 2 }}>
            {filteredActive.length === 0 ? (
              <Paper
                sx={{
                  p: 6,
                  textAlign: "center",
                  borderRadius: 3,
                  bgcolor: "#fff",
                  boxShadow: "0 2px 12px rgba(15,23,42,0.07)",
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    bgcolor: "rgba(124,58,237,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <AssignmentTurnedIn
                    sx={{ fontSize: 36, color: "#7c3aed" }}
                  />
                </Box>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ color: "#1e293b", mb: 0.5 }}
                >
                  {searchQuery
                    ? "No matching active orders"
                    : "No active orders"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                  {searchQuery
                    ? "Try a different search term"
                    : "Accept orders from notifications to see them here"}
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={2.5}>
                {filteredActive.map((order) => {
                  const status = getCurrentOrderStatus(order);
                  const statusSteps = [
                    { key: "ASSIGNED", label: "Assigned" },
                    { key: "PICKED_UP", label: "Picked Up" },
                    { key: "IN_TRANSIT", label: "In Transit" },
                    { key: "DELIVERED", label: "Delivered" },
                  ];
                  const currentStepIndex = statusSteps.findIndex(
                    (s) => s.key === status
                  );

                  return (
                    <Card
                      key={order._id}
                      sx={{
                        position: "relative",
                        borderRadius: 3.2,
                        border: "1px solid rgba(59,130,246,0.16)",
                        boxShadow: "0 14px 38px rgba(15,23,42,0.08)",
                        transition: "transform 0.18s ease, box-shadow 0.18s ease",
                        overflow: "hidden",
                        "&:hover": {
                          transform: "translateY(-3px)",
                          boxShadow: "0 18px 42px rgba(15,23,42,0.12)",
                        },
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          background:
                            "linear-gradient(90deg, #2563eb 0%, #8b5cf6 100%)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                        {/* Header */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 2,
                            mb: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              sx={{ color: "#1e293b", fontSize: "0.95rem" }}
                            >
                              Order #
                              {order._id.substring(0, 8).toUpperCase()}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#94a3b8" }}
                            >
                              {new Date(order.createdAt).toLocaleDateString(
                                "en-IN",
                                {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                }
                              )}
                            </Typography>
                          </Box>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ flexShrink: 0 }}
                          >
                            <Chip
                              label="Accepted"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                borderRadius: "999px",
                                bgcolor: "#eff6ff",
                                color: "#1d4ed8",
                                border: "1px solid rgba(59,130,246,0.18)",
                              }}
                            />
                            <Box sx={{ textAlign: "right" }}>
                              <Typography
                                variant="h6"
                                fontWeight={700}
                                sx={{
                                  color: "#1976d2",
                                  fontSize: "1.1rem",
                                }}
                              >
                                {formatCurrency(order.total)}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>

                        {/* Status Timeline */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: 2.5,
                            p: 1.5,
                            bgcolor: "#eff6ff",
                            borderRadius: 2,
                            border: "1px solid rgba(59,130,246,0.14)",
                          }}
                        >
                          {statusSteps.map((step, idx) => {
                            const isCompleted = idx <= currentStepIndex;
                            const isCurrent = idx === currentStepIndex;
                            return (
                              <React.Fragment key={step.key}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    flex: 1,
                                  }}
                                >
                                  {isCompleted ? (
                                    <CheckCircle
                                      sx={{
                                        fontSize: 16,
                                        color: isCurrent
                                          ? "#f59e0b"
                                          : "#22c55e",
                                      }}
                                    />
                                  ) : (
                                    <RadioButtonUnchecked
                                      sx={{
                                        fontSize: 16,
                                        color: "#cbd5e1",
                                      }}
                                    />
                                  )}
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: "0.62rem",
                                      fontWeight: isCurrent ? 700 : 500,
                                      color: isCompleted
                                        ? isCurrent
                                          ? "#92400e"
                                          : "#166534"
                                        : "#94a3b8",
                                      display: { xs: "none", sm: "block" },
                                    }}
                                  >
                                    {step.label}
                                  </Typography>
                                </Box>
                                {idx < statusSteps.length - 1 && (
                                  <Box
                                    sx={{
                                      flex: 1,
                                      height: 2,
                                      bgcolor: isCompleted
                                        ? "#22c55e"
                                        : "#e2e8f0",
                                      borderRadius: 1,
                                    }}
                                  />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </Box>

                        {/* Vendor & Customer Info */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 0.75,
                              }}
                            >
                              <Box
                                onClick={() => {
                                  const vendorAddressData = getVendorDetails(order).addressData;
                                  if (vendorAddressData) {
                                    openGoogleMapsDirections(vendorAddressData);
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    const vendorAddressData = getVendorDetails(order).addressData;
                                    if (vendorAddressData) {
                                      openGoogleMapsDirections(vendorAddressData);
                                    }
                                  }
                                }}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  bgcolor: "rgba(124,58,237,0.1)",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                  mt: 0.15,
                                  transition: "transform 0.18s ease, bgcolor 0.18s ease",
                                  "&:hover": {
                                    bgcolor: "rgba(124,58,237,0.16)",
                                    transform: "scale(1.04)",
                                  },
                                }}
                              >
                                <Storefront
                                  sx={{
                                    fontSize: 16,
                                    color: "#7c3aed",
                                  }}
                                />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#94a3b8",
                                    fontWeight: 500,
                                    display: "block",
                                  }}
                                >
                                  Pickup
                                </Typography>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  sx={{
                                    color: "#334155",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {getVendorName(order)}
                                </Typography>
                                {getVendorAddress(order) && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "#94a3b8",
                                      display: "block",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {getVendorAddress(order)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 0.75,
                              }}
                            >
                              <Box
                                onClick={() => {
                                  const deliveryAddress = order.address || order.shippingAddress || order.deliveryAddress;
                                  if (deliveryAddress) {
                                    openGoogleMapsDirections(deliveryAddress);
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    const deliveryAddress = order.address || order.shippingAddress || order.deliveryAddress;
                                    if (deliveryAddress) {
                                      openGoogleMapsDirections(deliveryAddress);
                                    }
                                  }
                                }}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  bgcolor: "rgba(25,118,210,0.1)",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                  mt: 0.15,
                                  transition: "transform 0.18s ease, bgcolor 0.18s ease",
                                  "&:hover": {
                                    bgcolor: "rgba(25,118,210,0.16)",
                                    transform: "scale(1.04)",
                                  },
                                }}
                              >
                                <Person
                                  sx={{
                                    fontSize: 16,
                                    color: "#1976d2",
                                  }}
                                />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#94a3b8",
                                    fontWeight: 500,
                                    display: "block",
                                  }}
                                >
                                  Delivery To
                                </Typography>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  sx={{ color: "#334155" }}
                                >
                                  {getCustomerName(order)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#94a3b8",
                                    display: "block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {getDeliveryAddress(order)}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Items Summary */}
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#94a3b8",
                              fontWeight: 500,
                              mb: 0.5,
                              display: "block",
                            }}
                          >
                            Items ({order.items.length})
                          </Typography>
                          <Stack spacing={0.75}>
                            {order.items.slice(0, 3).map((item, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  bgcolor: "#f8fafc",
                                  borderRadius: 1,
                                  px: 1.25,
                                  py: 0.6,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#475569", fontWeight: 500 }}
                                >
                                  {item.title}{" "}
                                  <Box
                                    component="span"
                                    sx={{ color: "#94a3b8" }}
                                  >
                                    x{item.qty}
                                  </Box>
                                </Typography>
                                <Typography
                                  variant="caption"
                                  fontWeight={600}
                                  sx={{ color: "#1e293b" }}
                                >
                                  {formatCurrency(item.price)}
                                </Typography>
                              </Box>
                            ))}
                            {order.items.length > 3 && (
                              <Typography
                                variant="caption"
                                sx={{ color: "#94a3b8", textAlign: "center" }}
                              >
                                +{order.items.length - 3} more items
                              </Typography>
                            )}
                          </Stack>
                        </Box>

                        {/* Distance & Payment */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mb: 2.5,
                            flexWrap: "wrap",
                          }}
                        >
                          {order.agent?.distanceKm && (
                            <Chip
                              icon={<LocalShipping />}
                              label={`${order.agent.distanceKm} km`}
                              variant="outlined"
                              size="small"
                              sx={{
                                fontSize: "0.7rem",
                                fontWeight: 500,
                                color: "#64748b",
                                borderColor: "#e2e8f0",
                              }}
                            />
                          )}
                          <Chip
                            icon={<Payment />}
                            label={
                              order.payment?.status === "PAID"
                                ? "Payment Completed"
                                : "Pay on Delivery"
                            }
                            color={
                              order.payment?.status === "PAID"
                                ? "success"
                                : "default"
                            }
                            variant="outlined"
                            size="small"
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 500,
                            }}
                          />
                        </Box>

                        {/* Action */}
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => goToOrderDetails(order._id)}
                          endIcon={<ArrowForward />}
                          sx={{
                            bgcolor: "#1976d2",
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.82rem",
                            py: 0.8,
                            "&:hover": {
                              bgcolor: "#1565c0",
                            },
                          }}
                        >
                          View & Update Status
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}

        {/* ═══════════════ DELIVERED ORDERS TAB ═══════════════ */}
        {tabValue === 2 && (
          <Box sx={{ mt: 2 }}>
            {filteredDelivered.length === 0 ? (
              <Paper
                sx={{
                  p: 6,
                  textAlign: "center",
                  borderRadius: 3,
                  bgcolor: "#fff",
                  boxShadow: "0 2px 12px rgba(15,23,42,0.07)",
                }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    bgcolor: "rgba(22,163,74,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <CheckCircle sx={{ fontSize: 36, color: "#16a34a" }} />
                </Box>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ color: "#1e293b", mb: 0.5 }}
                >
                  {searchQuery
                    ? "No matching delivered orders"
                    : "No delivered orders yet"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                  {searchQuery
                    ? "Try a different search term"
                    : "Completed deliveries will appear here"}
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={2.5}>
                {filteredDelivered.map((order) => (
                  <Card
                    key={order._id}
                    sx={{
                      position: "relative",
                      borderRadius: 3.2,
                      border: "1px solid rgba(22,163,74,0.18)",
                      boxShadow: "0 14px 38px rgba(15,23,42,0.07)",
                      transition: "transform 0.18s ease, box-shadow 0.18s ease",
                      overflow: "hidden",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: "0 18px 38px rgba(15,23,42,0.10)",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background:
                          "linear-gradient(90deg, #16a34a 0%, #4ade80 100%)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                      {/* Header */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 2,
                          mb: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: "50%",
                              bgcolor: "rgba(22,163,74,0.12)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <CheckCircle
                              sx={{ color: "#16a34a", fontSize: 24 }}
                            />
                          </Box>
                          <Box>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              sx={{ color: "#1e293b", fontSize: "0.95rem" }}
                            >
                              Order #
                              {order._id.substring(0, 8).toUpperCase()}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#64748b" }}
                            >
                              Delivered on{" "}
                              {order.deliveryPhase?.deliveredAt
                                ? new Date(
                                    order.deliveryPhase.deliveredAt
                                  ).toLocaleDateString("en-IN", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })
                                : new Date(order.createdAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ flexShrink: 0 }}
                        >
                          <Box sx={{ textAlign: "right" }}>
                            <Typography
                              variant="h6"
                              fontWeight={700}
                              sx={{ color: "#16a34a", fontSize: "1.1rem" }}
                            >
                              {formatCurrency(order.total)}
                            </Typography>
                          </Box>
                          <Chip
                            label="Delivered"
                            color="success"
                            size="small"
                            icon={<CheckCircle />}
                            sx={{
                              mt: 0.5,
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              borderRadius: "999px",
                              bgcolor: "#ecfdf5",
                            }}
                          />
                        </Stack>
                      </Box>

                      {/* Items */}
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#94a3b8",
                            fontWeight: 500,
                            mb: 0.5,
                            display: "block",
                          }}
                        >
                          Items ({order.items.length})
                        </Typography>
                        <Stack spacing={0.75}>
                          {order.items.slice(0, 3).map((item, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                bgcolor: "#f0fdf4",
                                borderRadius: 1,
                                px: 1.25,
                                py: 0.6,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "#475569", fontWeight: 500 }}
                              >
                                {item.title}{" "}
                                <Box
                                  component="span"
                                  sx={{ color: "#94a3b8" }}
                                >
                                  x{item.qty}
                                </Box>
                              </Typography>
                              <Typography
                                variant="caption"
                                fontWeight={600}
                                sx={{ color: "#1e293b" }}
                              >
                                {formatCurrency(item.price)}
                              </Typography>
                            </Box>
                          ))}
                          {order.items.length > 3 && (
                            <Typography
                              variant="caption"
                              sx={{ color: "#94a3b8", textAlign: "center" }}
                            >
                              +{order.items.length - 3} more items
                            </Typography>
                          )}
                        </Stack>
                      </Box>

                      {/* Delivery Address */}
                      {order.address && (
                        <Paper
                          sx={{
                            p: 1.5,
                            bgcolor: "#f0fdf4",
                            borderRadius: 2,
                            mb: 2,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#94a3b8",
                              fontWeight: 500,
                              display: "block",
                              mb: 0.5,
                            }}
                          >
                            <LocationOn
                              sx={{
                                fontSize: 13,
                                color: "#16a34a",
                                mr: 0.3,
                                verticalAlign: "middle",
                              }}
                            />
                            Delivered To
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#334155", fontWeight: 500 }}
                          >
                            {order.address.addressLine1}
                            {order.address.addressLine2 &&
                              `, ${order.address.addressLine2}`}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                            {order.address.city}
                            {order.address.state && `, ${order.address.state}`}
                            {order.address.pincode && ` - ${order.address.pincode}`}
                          </Typography>
                        </Paper>
                      )}

                      {/* Payment Status */}
                      <Box sx={{ mb: 2.5 }}>
                        <Chip
                          icon={<Payment />}
                          label={
                            order.payment?.status === "PAID"
                              ? "Payment Completed"
                              : "Pay on Delivery"
                          }
                          color={
                            order.payment?.status === "PAID"
                              ? "success"
                              : "default"
                          }
                          variant="outlined"
                          size="small"
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 500,
                          }}
                        />
                      </Box>

                      {/* Action */}
                      <Button
                        variant="contained"
                        fullWidth
                        color="success"
                        onClick={() => goToOrderDetails(order._id)}
                        endIcon={<Visibility />}
                        sx={{
                          borderRadius: "14px",
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: "0.86rem",
                          py: 0.95,
                          bgcolor: "#16a34a",
                          boxShadow: "0 10px 24px rgba(22,163,74,0.18)",
                          "&:hover": {
                            bgcolor: "#15803d",
                          },
                        }}
                      >
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {/* ═══════════════ CONFIRMATION DIALOG ═══════════════ */}
        <Dialog
          open={confirmDialog}
          onClose={() => setConfirmDialog(false)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxWidth: 400,
              width: "100%",
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#1e293b" }}>
              {dialogAction === "accept" ? "Accept Order?" : "Decline Order?"}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pb: 2 }}>
            {selectedNotification && (
              <Box>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#f8fafc",
                    borderRadius: 2,
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ color: "#1e293b", mb: 0.5 }}
                  >
                    {getNotificationVendorName(selectedNotification)}
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ color: "#1976d2", mb: 0.5 }}
                  >
                    {formatCurrency(getNotificationAmount(selectedNotification))}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Chip
                      icon={<LocalShipping />}
                      label={`${selectedNotification.distanceKm || 0} km`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.68rem" }}
                    />
                    <Chip
                      icon={<AccessTime />}
                      label={getTimeRemaining(selectedNotification.expiresAt)}
                      size="small"
                      color="warning"
                      sx={{ fontSize: "0.68rem" }}
                    />
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: "#64748b", fontSize: "0.82rem" }}
                >
                  {dialogAction === "accept"
                    ? "This order will be assigned to you and added to your active orders."
                    : "This order will be removed from your notifications."}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button
              onClick={() => setConfirmDialog(false)}
              disabled={loading}
              sx={{
                color: "#64748b",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                px: 2,
              }}
            >
              Cancel
            </Button>
            {dialogAction === "accept" ? (
              <Button
                onClick={handleAcceptOrder}
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: "#16a34a",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  "&:hover": { bgcolor: "#15803d" },
                }}
              >
                {loading ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  "Accept"
                )}
              </Button>
            ) : (
              <Button
                onClick={handleDeclineOrder}
                variant="contained"
                color="error"
                disabled={loading}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                }}
              >
                {loading ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  "Decline"
                )}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
      )}
    </Box>
  );
};

export default AgentOrders;